import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRedline } from '@libs/redline';
import { legacyBase64Decrypt, legacyBase64Encrypt } from '@libs/utils/helpers/base64';
import constants from '@common/constants';
import { pageNames } from '@common/routes/pageNames';
import { useDebts } from '@common/hooks/useDebts';
import { useUser } from '@common/hooks/useUser';
import usePix from '@common/hooks/usePix';
import { getFromLS } from '../../Confirmation/hooks/helpers';
import { ClosedAgreementProps } from '../types';
import useApiPartners from '@common/apis/partners/useApiPartners';
import useGTM from '@common/hooks/useGTM';
import useApiSortingDebt from '@common/apis/sorting-debt/useApiSortingDebt';
import { dateToLocale } from '@libs/utils/helpers/dateToLocale';
import { useAgreementsListQuery } from '@libs/renegkit/queries/agreements';

import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';
import useAuth from '@common/hooks/useAuth';
import useSimulatedDebt from '@common/hooks/useSimulatedDebt';
import useBilletHandler from '@common/contexts/RenegContext/agreements/hooks/useBilletHandler';
import { checkGenerateClosedAgreementBillet } from '@common/contexts/RenegContext/agreements/services/shouldGenerateBillet';
import { GenerateBilletResponse } from '@common/apis/billet/types';

import * as itau from '@dues/helpers/itau';
import { routes } from '@libs/renegkit/routes';

const useClosedAgreement = () => {
  const { document } = useUser();
  const navigate = useNavigate();

  const warningRef = useRef(null);
  const ctrlSendErrorPix = useRef(true);
  const ctrlSendPixPaid = useRef(true);
  const { apiSortingDebtInvalidateCache } = useApiSortingDebt();
  const { cleanSimulatedDebtsCache } = useSimulatedDebt();
  const { invalidateAgreements } = useAgreementsListQuery(false);

  const GTM = useGTM();
  const { track: trackRedline } = useRedline();

  const { createDebtData } = useDebts();

  const [closedAgreement] = useState<ClosedAgreementProps>(() => getFromLS(constants.debts.CLOSED_AGREEMENT));
  const retryError = !!closedAgreement?.isRetryError;

  const { apiCheckIfCanUpdateAgreement, apiUpdateAgreement } = useApiPartners(closedAgreement?.debt?.partner?.msUrl);
  const { newBilletPolling, isPollingBillet, billet, onFinishFirstBilletReq } = useBilletHandler({
    msUrl: closedAgreement?.debt?.partner?.msUrl,
    closedAgreement,
    installment: closedAgreement?.agreement?.acordoParcelas?.[0],
  });

  //#region ACP
  const { logOut } = useAuth();
  const { authenticatedUser, tokenOrigin } = useAuthenticatedUser();
  const isNCADCheckout = authenticatedUser?.customer?.required_updates?.includes('account_ncad');
  const isNCADGeneral = !isNCADCheckout && tokenOrigin() === 'ncad';
  // #endregion
  const [debtData] = useState(
    createDebtData({
      debt: closedAgreement?.debt,
      paymentOption: closedAgreement?.paymentOption,
      paymentMethod: closedAgreement?.paymentMethod,
    })
  );

  const isItauDesenrola =
    closedAgreement?.isDesenrola ??
    itau.isDesenrolaItau(closedAgreement?.debt);

  const {
    paymentStatus,
    setPaymentStatus,
    pixData,
    isPixExternal,
    pixCode,
    qrCodeUrl,

    handleCopyPixCode,
    buttonText: pixText,
    isPixSuccess,
    isLoadingCAPix,
  } = usePix({
    debt: closedAgreement?.debt,
    paymentOption: closedAgreement?.paymentOption,
    installment: closedAgreement?.agreement?.acordoParcelas?.[0],
    agreement: closedAgreement?.agreement,

    closedAgreement,
    refCopiedWarning: warningRef,
    billetCode: billet?.linhaDigitavel || '',
    isPollingBillet,
  });

  useEffect(() => {
    invalidateAgreements();
  }, []);

  const isFirstInstallmentPaid = paymentStatus === 'pixPaid';

  function getFirstInstallmentExpiration() {
    const hasAvailableDates = closedAgreement?.paymentOption?.propriedades?.some((prop) =>
      prop?.chave?.includes(constants.debts.PROPERTIES.PAYMENT_DATE)
    );
    const showSelectPaymentDate = hasAvailableDates && debtData?.identifier === 'recovery';

    const installmentDueDate = dateToLocale(closedAgreement?.agreement?.acordoParcelas?.[0]?.dataVencimento);
    const dueDate = showSelectPaymentDate ? installmentDueDate : debtData?.paymentOpt?.dueDate;

    return dueDate;
  }

  const installmentCode = closedAgreement?.agreement?.acordoParcelas?.[0]?.installmentCode;

  const payload = {
    installmentCode: installmentCode,
    parameters: {
      partnerSystemIdentifier: closedAgreement?.debt?.partner?.identifier,
      requestParameters: {
        dividaId: closedAgreement?.debt?.id,
        opcaoPagamentoId: closedAgreement?.paymentOption?.id,
      },
    },
  };

  const newGenerateBilletPayload = {
    payload,
    onBilletGenerated: (response: GenerateBilletResponse) => {
      const closedAgreementFromLocal = localStorage.getItem(constants.debts.CLOSED_AGREEMENT) || '';
      const descryptClosedAgreement = JSON.parse(legacyBase64Decrypt(closedAgreementFromLocal));

      const updatedClosedAgreement = {
        ...descryptClosedAgreement,
        billetData: response,
      };
      const encryptClosedAgreement = legacyBase64Encrypt(JSON.stringify(updatedClosedAgreement));

      localStorage.setItem(constants.debts.CLOSED_AGREEMENT, encryptClosedAgreement);
    },
    onBilletErrored: (err: any) => {
      console.error('[CLOSED AGREEMENT - ERROR GENERATING BILLET]', err);
    },
  };

  const trackPix = () => {
    if (!closedAgreement) return; // TODO: pixCode >> || !pixCode
    const { agreement, debt, paymentOption } = closedAgreement;

    const isStatusErrorPix = paymentStatus === 'error';
    const isStatusPixPaidPix = paymentStatus === 'pixPaid';

    if (isStatusErrorPix && ctrlSendErrorPix.current) {
      ctrlSendErrorPix.current = false;
    }

    if (isStatusPixPaidPix && ctrlSendPixPaid.current) {
      ctrlSendPixPaid.current = false;

      trackRedline.debts.pixPayed({
        pixCode: pixData?.data?.pix_code,
        paymentOption,
        debt,
        agreement,
      });

      GTM.trigger.pixPayed({ valorAcordo: agreement?.valorAcordo });
    }
  };

  const handleUpdateContactData = () => {
    const el = window.document.getElementById('update-contact-data');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });

    trackRedline.userTracking.elementClicked({
      elementType: 'link',
      name: 'link-update-contact-data',
      text: 'Atualizar dados',
      location: window.location.pathname,
    });
  };

  const handleUpdateAgreement = (request: any) => {
    const statusPixPaid = 'pixPaid';
    const SECONDS_IN_MS = 10000;

    const intervalUpdAgreement = setInterval(() => {
      apiUpdateAgreement
        .send(request)
        .then((response) => {
          const currentInstallment = response?.installments?.[0];
          const situationInstallment = currentInstallment?.situation;

          if (situationInstallment === 'PAYMENT_COMPLETED') {
            setPaymentStatus(statusPixPaid);
            localStorage.setItem(constants.debts.PIX_STATUS, legacyBase64Encrypt(statusPixPaid));

            clearInterval(intervalUpdAgreement);
            return;
          }
        })
        .catch((err: any) => {
          console.error('[handleUpdateAgreement]:', err);
        });
    }, SECONDS_IN_MS);
  };

  const handleIfCanUpdateAgreement = () => {
    const { partnerIdentifier } = closedAgreement?.debt?.partner || {};
    const { PARTNERS_WHO_CAN_UPDATE_AGREEMENT } = constants.debts;

    const hasUpdateAgreement = PARTNERS_WHO_CAN_UPDATE_AGREEMENT.includes(partnerIdentifier);

    if (!hasUpdateAgreement || paymentStatus !== 'pending') return;

    const request = {
      agreementId: closedAgreement?.agreement?.id,
      documento: document,
    };

    apiCheckIfCanUpdateAgreement
      .send(request)
      .then((response) => {
        const hasUpdate = response;

        if (hasUpdate) handleUpdateAgreement(request);
      })
      .catch((err) => {
        console.error('handleIfCanUpdateAgreement ', err);
      });
  };

  const handleSeeAgreementDetails = () => {
    if (isNCADCheckout || isNCADGeneral) {
      const document = authenticatedUser.customer.document;
      sessionStorage.removeItem('temp_closedAgreementRedirect');

			const event = new CustomEvent("logout_trail", {
				detail: {
					trigger: 'handle_see_agreement_details_ncad',
					token: authenticatedUser.auth.access_token
				},
			});

			window.dispatchEvent(event);

      logOut();

      const targetPage = pageNames.signupNCADCompleted.path;
      navigate(`${targetPage}?c=${legacyBase64Encrypt(document)}`, { replace: true });

      return;
    }

    const path = routes.agreement.path.replace(':agreementId', String(closedAgreement?.agreement?.uuid));
    navigate(path);
  };

  const handleRedirectToMyDebts = () => {
    navigate(pageNames.myDebts.path);
  };

  useEffect(() => {
    trackPix();

    handleIfCanUpdateAgreement();
  }, [paymentStatus]);

  useEffect(() => {
    window.document.body.classList.toggle('no-scroll', false);

    apiSortingDebtInvalidateCache.send({
      documento: document,
      provider_identifier: closedAgreement?.debt?.partner?.identifier,
    });
    cleanSimulatedDebtsCache();
  }, []);

  const BILLET_CHECK_INTERVAL_MS = 1000;

  useEffect(() => {
    const interval = setInterval(() => {
      const billetRequest = localStorage.getItem(constants.debts.BILLET_REQ);
      const isBilletPending = billetRequest === 'pending';
      const shouldRetryBillet = checkGenerateClosedAgreementBillet({ agreement: closedAgreement, debtData });

      if (billet?.linhaDigitavel || retryError || !shouldRetryBillet || isBilletPending) return;

      if (billetRequest && !isBilletPending) {
        try {
          const foundBilletData = JSON.parse(billetRequest);
          if (foundBilletData?.linhaDigitavel) {
            onFinishFirstBilletReq(foundBilletData);
            clearInterval(interval);
          } else {
            const alreadyPolled = localStorage.getItem(constants.debts.BILLET_POLL);
            if (!alreadyPolled) newBilletPolling(newGenerateBilletPayload);
          }
        } catch (e) {
          console.error('Error parsing BILLET_REQ', e);
        }
        return;
      }
    }, BILLET_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [billet]);

  useEffect(() => {
    const hasBilletPostponed = sessionStorage.getItem(constants.debts.BILLET_POSTPONED);
    if (billet?.linhaDigitavel && hasBilletPostponed === 'true') {
      sessionStorage.removeItem(constants.debts.BILLET_POSTPONED);
      trackRedline.debts.billetGenerated({
        barcode: billet?.linhaDigitavel,
        paymentOption: closedAgreement.paymentOption,
        debt: closedAgreement?.debt,
        agreement: closedAgreement,
      });
    }
  }, [billet?.linhaDigitavel]);

  return {
    closedAgreement,
    debtData,
    qrCodeUrl,
    isPixExternal,
    pixText,
    billet,
    hasBillet: !!billet?.linhaDigitavel,
    isPollingBillet,
    handleCopyPixCode,
    isPixSuccess,
    pixCode,
    warningRef,
    selectedBankAccount: closedAgreement?.debitAccount?.selectedBankAccount,
    shouldRedirectToDebitAccount: !!closedAgreement?.debitAccount?.redirectToDebitAccount,
    firstInstallmentExpDate: getFirstInstallmentExpiration(),
    isFirstInstallmentPaid,
    handleUpdateContactData,
    handleSeeAgreementDetails,
    handleRedirectToMyDebts,
    retryError,
    isLoadingCAPix,
    isItauDesenrola,
  };
};
export default useClosedAgreement;
